/**
 * Story 8.18 — search uses listing tags.
 *
 * The listing tags (Story 8.15) live on `publicData.proseTags`, a CLI-set
 * multi-enum search key, so a search narrows to them with
 * `pub_proseTags=has_any:<slug>`. This module maps the chips the AI parser
 * already returns to those slugs, deterministically (ID-2), and names the chip
 * a buyer sees for each (ID-5).
 *
 * 🔴 LISTING_TAG_VOCABULARY is a PINNED COPY of tph-api's
 * `src/services/listingTagVocabulary.js`. `listingTagSearch.test.js` asserts it
 * against `__fixtures__/listingTagVocabulary.<date>.json`, captured from a named
 * tph-api SHA. The suites do not cross repos, so a tph-api vocabulary change
 * fails nothing here: re-capture on any edit to that file (ledger #3101).
 */

import { parseSelectFilterOptions } from './search';
import { stringify } from './urlHelpers';

export const TAG_PARAM = 'pub_proseTags';

export const LISTING_TAG_VOCABULARY = Object.freeze([
  Object.freeze({ slug: '1d', label: '1D' }),
  Object.freeze({ slug: '2d', label: '2D' }),
  Object.freeze({ slug: '3d', label: '3D' }),
  Object.freeze({ slug: 'earnings', label: 'Earnings' }),
  Object.freeze({ slug: 'futurity', label: 'Futurity / derby' }),
  Object.freeze({ slug: 'jackpot', label: 'Jackpot' }),
  Object.freeze({ slug: 'rodeo', label: 'Rodeo' }),
  Object.freeze({ slug: 'hauls', label: 'Hauls' }),
  Object.freeze({ slug: 'prospect', label: 'Prospect' }),
  Object.freeze({ slug: 'finished', label: 'Finished' }),
  Object.freeze({ slug: 'beginner', label: 'Beginner suitable' }),
]);

// The words a buyer sees in the "Described as: …" chip — the story card's
// Exact copy, approved word for word (2026-10-05). Two differ from the
// vocabulary labels above (futurity, beginner); the card wins.
export const TAG_CHIP_NAMES = Object.freeze({
  '1d': '1D',
  '2d': '2D',
  '3d': '3D',
  earnings: 'Earnings',
  futurity: 'Futurity',
  jackpot: 'Jackpot',
  rodeo: 'Rodeo',
  hauls: 'Hauls',
  prospect: 'Prospect',
  finished: 'Finished',
  beginner: 'Beginner',
});

// Story 8.19 (Task 4) — the SELLER panel's own names for the 11 slugs ("How
// buyers will find this horse", the wizard panel's Exact Copy, approved word
// for word by Ryan Bennett 2026-10-09). This is a FOURTH label set for the
// same 11 slugs — do not conflate it with `TAG_CHIP_NAMES` above (the BUYER
// "Described as: …" chip, 8.18) or with tph-api's internal `label`
// (`listingTagVocabulary.js`, the operator's Tag Labelling board). Three
// differ from the buyer chip's wording (earnings, hauls, beginner) — "the
// card wins" on every one of those three.
export const SELLER_PANEL_TAG_LABELS = Object.freeze({
  '1d': '1D',
  '2d': '2D',
  '3d': '3D',
  earnings: 'Money earner',
  futurity: 'Futurity',
  jackpot: 'Jackpot',
  rodeo: 'Rodeo',
  hauls: 'Hauls well',
  prospect: 'Prospect',
  finished: 'Finished',
  beginner: 'Beginner-friendly',
});

/**
 * The name a seller sees for a slug in the wizard's search-tag panel. A slug
 * outside the vocabulary (a stale stored value) shows as itself, same
 * fail-open posture as `tagChipName` above.
 *
 * @param {string} slug
 * @returns {string}
 */
export const sellerPanelTagLabel = slug =>
  Object.prototype.hasOwnProperty.call(SELLER_PANEL_TAG_LABELS, slug)
    ? SELLER_PANEL_TAG_LABELS[slug]
    : slug;

const VOCABULARY_SLUGS = LISTING_TAG_VOCABULARY.map(entry => entry.slug);

// Every slug maps to itself; `derby` is named by the futurity rule itself, and
// the singulars of the two plural slugs are variants too (operator ruling R-B,
// 2026-10-06, ledger #3100: "haul" → hauls, "earning" → earnings).
// A Map, not an object, so no inherited key ("constructor") can ever resolve.
const TAG_LEXICON = new Map([
  ...VOCABULARY_SLUGS.map(slug => [slug, slug]),
  ['derby', 'futurity'],
  ['haul', 'hauls'],
  ['earning', 'earnings'],
]);

// Plurals and variants map to the same tag (the card's Product question 4):
// "prospects" and "1ds" drop one trailing s, "futurities" and "derbies" end
// in y. The apostrophe is removed before splitting, so "1D's" reads "1ds".
const slugForToken = token => {
  if (TAG_LEXICON.has(token)) {
    return TAG_LEXICON.get(token);
  }
  if (token.endsWith('ies')) {
    const singular = `${token.slice(0, -3)}y`;
    if (TAG_LEXICON.has(singular)) {
      return TAG_LEXICON.get(singular);
    }
  }
  if (token.endsWith('s')) {
    const singular = token.slice(0, -1);
    if (TAG_LEXICON.has(singular)) {
      return TAG_LEXICON.get(singular);
    }
  }
  return null;
};

// A chip maps only when EVERY token of its search_term is a tag word — a sire
// name that happens to contain "rodeo", or "1D 15.1 hands", maps nothing,
// because a false tag silently narrows away the horses the buyer named.
const slugsForTerm = term => {
  const tokens = term
    .toLowerCase()
    .replace(/['’]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  if (tokens.length === 0) {
    return [];
  }
  const slugs = tokens.map(slugForToken);
  return slugs.every(Boolean) ? slugs : [];
};

/**
 * The listing-tag slugs a search's unmatched chips name: only `searchable`
 * chips with a non-empty `search_term` (the chips `searchKeywordsFromChips`
 * sends as keywords), deduplicated, in vocabulary order.
 *
 * @param {Array<Object>} chips `unmatched_keywords` from /search/route
 * @returns {Array<string>} slugs, or [] when none map
 */
export const tagSlugsFromChips = chips => {
  if (!Array.isArray(chips)) {
    return [];
  }
  const found = new Set();
  chips.forEach(chip => {
    const term =
      chip && chip.class === 'searchable' && typeof chip.search_term === 'string'
        ? chip.search_term.trim()
        : '';
    if (term) {
      slugsForTerm(term).forEach(slug => found.add(slug));
    }
  });
  return VOCABULARY_SLUGS.filter(slug => found.has(slug));
};

/**
 * The `pub_proseTags` URL param for a search's chips, or {} when no chip maps —
 * never an empty value.
 *
 * @param {Array<Object>} chips `unmatched_keywords` from /search/route
 * @returns {Object} `{ pub_proseTags: 'has_any:<slugs>' }` or `{}`
 */
export const tagParamFromChips = chips => {
  const slugs = tagSlugsFromChips(chips);
  return slugs.length > 0 ? { [TAG_PARAM]: `has_any:${slugs.join(',')}` } : {};
};

/**
 * The name a "Described as: …" chip shows for a slug. A slug outside the
 * vocabulary (a hand-edited or stale URL) shows as itself, so the filter is
 * never hidden from the buyer.
 *
 * @param {string} slug
 * @returns {string}
 */
export const tagChipName = slug =>
  Object.prototype.hasOwnProperty.call(TAG_CHIP_NAMES, slug) ? TAG_CHIP_NAMES[slug] : slug;

/**
 * The slugs a `pub_proseTags` URL value names, in URL order, deduplicated — one
 * chip per slug, so a hand-edited `has_any:1d,1d` cannot render two chips that
 * share a React key and vanish together (step 5 review L4).
 *
 * @param {string} value the raw param value, e.g. `has_any:1d,2d`
 * @returns {Array<string>}
 */
export const tagSlugsFromParam = value =>
  Array.from(new Set(parseSelectFilterOptions(`${value || ''}`).filter(Boolean)));

const TAG_MODE_PREFIX = /^has_(any|all):/;
// Not filter params: the existing filter handler drops both on any filter
// change (validUrlQueryParamsFromProps), so a narrower or wider result set
// always starts again at page 1.
const PAGE_ONLY_PARAMS = ['page', 'mapSearch'];

const segmentKey = segment => {
  const keys = Array.from(new URLSearchParams(segment).keys());
  return keys.length > 0 ? keys[0] : null;
};

/**
 * Story 8.18 step 6 (review M2) — the query string after one "Described as"
 * chip's ✕. Every other param is kept BYTE-IDENTICAL, segment for segment: a
 * tag removal must never re-normalise another filter, and the hosted-filter
 * handler rebuilds and re-prefixes every filter it touches, so the chip does
 * not go through it. Only the tag segment is
 * rewritten — the remaining slugs under the value's own mode prefix (or none,
 * as written) — and `page` / `mapSearch` are dropped.
 *
 * @param {string} search `location.search`, with or without the leading `?`
 * @param {string} slug the slug whose chip was removed
 * @returns {string} the new query string without a leading `?` ('' when empty)
 */
export const searchWithoutTagSlug = (search, slug) => {
  const raw = `${search || ''}`.replace(/^\?/, '');
  const segments = raw ? raw.split('&') : [];
  // parse() keeps the LAST value of a repeated key, so the chips and loadData
  // see that one; rebuild from it too.
  const values = new URLSearchParams(raw).getAll(TAG_PARAM);
  const current = values.length > 0 ? values[values.length - 1] : '';
  const modeMatch = current.match(TAG_MODE_PREFIX);
  const mode = modeMatch ? modeMatch[0] : '';
  const remaining = tagSlugsFromParam(current).filter(s => s !== slug);
  const nextValue = remaining.length > 0 ? `${mode}${remaining.join(',')}` : null;

  let tagPlaced = false;
  const kept = segments.reduce((result, segment) => {
    const key = segmentKey(segment);
    if (PAGE_ONLY_PARAMS.includes(key)) {
      return result;
    }
    if (key !== TAG_PARAM) {
      return [...result, segment];
    }
    if (tagPlaced || nextValue == null) {
      tagPlaced = true;
      return result;
    }
    tagPlaced = true;
    return [...result, stringify({ [TAG_PARAM]: nextValue })];
  }, []);
  return kept.join('&');
};
